import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTracker } from '../../state/store';
import { audioEngine } from '../../engine/audio';
import {
  LOOP_FORWARD,
  LOOP_NONE,
  LOOP_PINGPONG,
  createSample,
  type LoopType,
  type Sample,
} from '../../engine/types';
import { Button, Field, NumberInput, Panel, Select, TextInput } from './primitives';

/**
 * Waveform display with draggable loop points.
 *
 * Long samples are drawn as a min/max envelope per pixel column rather than by
 * plotting every frame — a two-second 44.1 kHz sample is 88,000 points, and
 * drawing them all is both slow and less legible than the envelope.
 */
function Waveform({
  sample,
  onLoopChange,
}: {
  sample: Sample | null;
  onLoopChange: (start: number, length: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 400, height: 130 });
  const dragging = useRef<'start' | 'end' | null>(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setSize({ width: el.clientWidth, height: el.clientHeight }));
    observer.observe(el);
    setSize({ width: el.clientWidth, height: el.clientHeight });
    return () => observer.disconnect();
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const { width, height } = size;
    canvas.width = Math.max(1, width * dpr);
    canvas.height = Math.max(1, height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.fillStyle = '#0b0b0e';
    ctx.fillRect(0, 0, width, height);

    const mid = height / 2;
    ctx.strokeStyle = '#22222a';
    ctx.beginPath();
    ctx.moveTo(0, mid);
    ctx.lineTo(width, mid);
    ctx.stroke();

    if (!sample || sample.length === 0) {
      ctx.fillStyle = '#3d3d4a';
      ctx.font = '11px "Spline Sans Mono", monospace';
      ctx.textAlign = 'center';
      ctx.fillText('no sample data', width / 2, mid);
      ctx.textAlign = 'left';
      return;
    }

    const framesPerPixel = sample.length / width;

    // Loop region behind the waveform.
    if (sample.loopType !== LOOP_NONE && sample.loopLength > 0) {
      const x0 = (sample.loopStart / sample.length) * width;
      const x1 = ((sample.loopStart + sample.loopLength) / sample.length) * width;
      ctx.fillStyle = 'rgba(6,193,103,0.10)';
      ctx.fillRect(x0, 0, x1 - x0, height);
    }

    ctx.strokeStyle = '#4ceb9b';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let x = 0; x < width; x++) {
      const from = Math.floor(x * framesPerPixel);
      const to = Math.min(sample.length, Math.floor((x + 1) * framesPerPixel) + 1);
      let min = 1;
      let max = -1;
      for (let i = from; i < to; i++) {
        const v = sample.data[i];
        if (v < min) min = v;
        if (v > max) max = v;
      }
      if (from >= to) {
        min = max = sample.data[Math.min(from, sample.length - 1)] ?? 0;
      }
      ctx.moveTo(x + 0.5, mid - max * mid * 0.95);
      ctx.lineTo(x + 0.5, mid - min * mid * 0.95);
    }
    ctx.stroke();

    // Loop markers on top.
    if (sample.loopType !== LOOP_NONE && sample.loopLength > 0) {
      const x0 = (sample.loopStart / sample.length) * width;
      const x1 = ((sample.loopStart + sample.loopLength) / sample.length) * width;
      for (const [x, label] of [
        [x0, 'S'],
        [x1, 'E'],
      ] as const) {
        ctx.fillStyle = '#06c167';
        ctx.fillRect(x - 1, 0, 2, height);
        ctx.fillStyle = '#06c167';
        ctx.fillRect(x - 6, 0, 12, 12);
        ctx.fillStyle = '#000';
        ctx.font = 'bold 9px "Spline Sans Mono", monospace';
        ctx.textAlign = 'center';
        ctx.fillText(label, x, 9);
        ctx.textAlign = 'left';
      }
    }
  }, [sample, size]);

  useEffect(() => {
    draw();
  }, [draw]);

  const frameAtX = (clientX: number) => {
    const canvas = canvasRef.current;
    if (!canvas || !sample) return 0;
    const rect = canvas.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    return Math.max(0, Math.min(sample.length, Math.round(ratio * sample.length)));
  };

  const onPointerDown = (event: React.PointerEvent) => {
    if (!sample || sample.loopType === LOOP_NONE) return;
    const frame = frameAtX(event.clientX);
    const distStart = Math.abs(frame - sample.loopStart);
    const distEnd = Math.abs(frame - (sample.loopStart + sample.loopLength));
    dragging.current = distStart < distEnd ? 'start' : 'end';
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent) => {
    if (!dragging.current || !sample) return;
    const frame = frameAtX(event.clientX);
    if (dragging.current === 'start') {
      const start = Math.min(frame, sample.loopStart + sample.loopLength - 1);
      onLoopChange(start, sample.loopStart + sample.loopLength - start);
    } else {
      const end = Math.max(frame, sample.loopStart + 1);
      onLoopChange(sample.loopStart, end - sample.loopStart);
    }
  };

  return (
    <div ref={containerRef} className="h-[130px] w-full overflow-hidden rounded border border-ink-700">
      <canvas
        ref={canvasRef}
        style={{ width: size.width, height: size.height }}
        className="block cursor-col-resize"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={() => (dragging.current = null)}
      />
    </div>
  );
}

export function SampleEditor() {
  const song = useTracker((s) => s.song);
  const currentInstrument = useTracker((s) => s.currentInstrument);
  const revision = useTracker((s) => s.revision);
  const replaceInstrument = useTracker((s) => s.replaceInstrument);
  const [sampleIndex, setSampleIndex] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const instrument = song.instruments[currentInstrument - 1];
  const sample = instrument?.samples[sampleIndex] ?? null;

  useEffect(() => {
    setSampleIndex(0);
  }, [currentInstrument]);

  /** Mutate the current sample and republish the instrument to the worklet. */
  const update = (changes: Partial<Sample>) => {
    if (!instrument || !sample) return;
    Object.assign(sample, changes);
    replaceInstrument(currentInstrument - 1, instrument);
  };

  const loadFile = async (file: File) => {
    if (!instrument) return;
    setBusy('decoding…');
    try {
      const buffer = await file.arrayBuffer();
      // decodeAudioData handles WAV, MP3, OGG, FLAC and AIFF, which is far more
      // useful than accepting raw 8-bit data the way the original trackers did.
      const ctx = new (window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      const decoded = await ctx.decodeAudioData(buffer.slice(0));
      await ctx.close();

      // Downmix to mono: tracker samples are single-channel.
      const frames = decoded.length;
      const data = new Float32Array(frames);
      for (let c = 0; c < decoded.numberOfChannels; c++) {
        const channel = decoded.getChannelData(c);
        for (let i = 0; i < frames; i++) data[i] += channel[i];
      }
      if (decoded.numberOfChannels > 1) {
        for (let i = 0; i < frames; i++) data[i] /= decoded.numberOfChannels;
      }

      const next = createSample(file.name.replace(/\.[^.]+$/, '').slice(0, 22));
      next.data = data;
      next.length = frames;
      next.bits = 16;
      next.volume = 64;

      // Retune so the sample plays at its original pitch on C-4. Tracker
      // playback rate for C-4 is 8363 Hz, so anything else needs transposing.
      const ratio = decoded.sampleRate / 8363;
      const semitones = 12 * Math.log2(ratio);
      next.relativeNote = Math.round(semitones);
      next.finetune = Math.max(-128, Math.min(127, Math.round((semitones - next.relativeNote) * 128)));

      if (instrument.samples.length === 0) instrument.samples.push(next);
      else instrument.samples[sampleIndex] = next;
      if (!instrument.name) instrument.name = next.name;

      replaceInstrument(currentInstrument - 1, instrument);
      setBusy(null);
    } catch (error) {
      setBusy(error instanceof Error ? `failed: ${error.message}` : 'failed to decode');
      setTimeout(() => setBusy(null), 4000);
    }
  };

  const transform = (fn: (data: Float32Array) => void, label: string) => {
    if (!sample) return;
    fn(sample.data);
    setBusy(label);
    setTimeout(() => setBusy(null), 900);
    replaceInstrument(currentInstrument - 1, instrument!);
  };

  const normalise = () =>
    transform((data) => {
      let peak = 0;
      for (const v of data) peak = Math.max(peak, Math.abs(v));
      if (peak > 0.0001) {
        const gain = 0.98 / peak;
        for (let i = 0; i < data.length; i++) data[i] *= gain;
      }
    }, 'normalised');

  const reverse = () =>
    transform((data) => {
      data.reverse();
    }, 'reversed');

  const fadeIn = () =>
    transform((data) => {
      for (let i = 0; i < data.length; i++) data[i] *= i / data.length;
    }, 'faded in');

  const fadeOut = () =>
    transform((data) => {
      for (let i = 0; i < data.length; i++) data[i] *= 1 - i / data.length;
    }, 'faded out');

  return (
    <Panel
      title="Sample"
      actions={
        busy && <span className="font-mono text-[10px] text-acid-400">{busy}</span>
      }
    >
      <div key={revision} className="flex flex-col gap-3">
        {instrument && instrument.samples.length > 1 && (
          <div className="flex flex-wrap gap-1">
            {instrument.samples.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setSampleIndex(i)}
                className={`rounded px-2 py-0.5 font-mono text-[10px] ${
                  i === sampleIndex ? 'bg-acid-900 text-acid-300' : 'bg-ink-800 text-ink-400 hover:text-white'
                }`}
              >
                {i}: {s.name || '—'}
              </button>
            ))}
          </div>
        )}

        <Waveform
          sample={sample}
          onLoopChange={(start, length) => update({ loopStart: start, loopLength: length })}
        />

        <div className="flex flex-wrap items-center gap-1">
          <Button onClick={() => fileRef.current?.click()} tone="accent">
            Load audio…
          </Button>
          <Button onClick={normalise} disabled={!sample}>
            Normalise
          </Button>
          <Button onClick={reverse} disabled={!sample}>
            Reverse
          </Button>
          <Button onClick={fadeIn} disabled={!sample}>
            Fade in
          </Button>
          <Button onClick={fadeOut} disabled={!sample}>
            Fade out
          </Button>
          <Button
            onClick={() => audioEngine.noteOn(7, 49, currentInstrument)}
            disabled={!sample}
            title="Preview at C-4"
          >
            ▶ Test
          </Button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="audio/*,.wav,.aif,.aiff,.mp3,.ogg,.flac"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void loadFile(file);
            e.target.value = '';
          }}
        />

        {sample && (
          <div className="grid grid-cols-2 gap-x-4">
            <Field label="Name">
              <TextInput
                value={sample.name}
                onChange={(v) => update({ name: v.slice(0, 22) })}
                className="w-32"
              />
            </Field>
            <Field label="Volume">
              <NumberInput value={sample.volume} min={0} max={64} onChange={(v) => update({ volume: v })} />
            </Field>
            <Field label="Finetune">
              <NumberInput
                value={sample.finetune}
                min={-128}
                max={127}
                onChange={(v) => update({ finetune: v })}
              />
            </Field>
            <Field label="Rel. note">
              <NumberInput
                value={sample.relativeNote}
                min={-96}
                max={95}
                onChange={(v) => update({ relativeNote: v })}
              />
            </Field>
            <Field label="Panning">
              <NumberInput value={sample.panning} min={0} max={255} onChange={(v) => update({ panning: v })} />
            </Field>
            <Field label="Loop">
              <Select
                value={String(sample.loopType) as '0' | '1' | '2'}
                onChange={(v) => {
                  const loopType = Number(v) as LoopType;
                  const changes: Partial<Sample> = { loopType };
                  // Turning a loop on with no region set would be a no-op, so
                  // default it to the whole sample.
                  if (loopType !== LOOP_NONE && sample.loopLength <= 0) {
                    changes.loopStart = 0;
                    changes.loopLength = sample.length;
                  }
                  update(changes);
                }}
                options={[
                  { value: '0', label: 'None' },
                  { value: '1', label: 'Forward' },
                  { value: '2', label: 'Ping-pong' },
                ]}
              />
            </Field>
            <Field label="Loop start">
              <NumberInput
                value={sample.loopStart}
                min={0}
                max={Math.max(0, sample.length - 1)}
                width="w-24"
                onChange={(v) => update({ loopStart: v })}
              />
            </Field>
            <Field label="Loop len">
              <NumberInput
                value={sample.loopLength}
                min={0}
                max={Math.max(0, sample.length - sample.loopStart)}
                width="w-24"
                onChange={(v) => update({ loopLength: v })}
              />
            </Field>
            <div className="col-span-2 pt-1 font-mono text-[10px] text-ink-500">
              {sample.length.toLocaleString()} frames · {sample.bits}-bit ·{' '}
              {sample.loopType === LOOP_FORWARD
                ? 'forward loop'
                : sample.loopType === LOOP_PINGPONG
                  ? 'ping-pong loop'
                  : 'one-shot'}
            </div>
          </div>
        )}

        {!sample && (
          <p className="font-mono text-[11px] leading-relaxed text-ink-500">
            This instrument has no sample. Load an audio file to get started — WAV, AIFF, MP3, OGG and
            FLAC all work, and the pitch is matched to C-4 automatically.
          </p>
        )}
      </div>
    </Panel>
  );
}
